import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import en from "../locales/en.json";
import zhCN from "../locales/zh-CN.json";

export type Locale = "zh-CN" | "en";
type Parameters = Record<string, string | number | null | undefined>;
type Dictionary = Record<string, string>;

const dictionaries: Record<Locale, Dictionary> = {
  "zh-CN": zhCN,
  en
};

export interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  applyPreferredLocale: (locale: Locale) => void;
  t: (key: string, parameters?: Parameters) => string;
  has: (key: string) => boolean;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function validLocale(value: string | null): value is Locale {
  return value === "zh-CN" || value === "en";
}

export function initialLocale(): Locale {
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  if (validLocale(fromUrl)) return fromUrl;
  const stored = window.localStorage.getItem("coms-platform-locale");
  if (validLocale(stored)) return stored;
  if (window.navigator.language.toLowerCase().startsWith("en")) return "en";
  if (window.navigator.language.toLowerCase().startsWith("zh")) return "zh-CN";
  return "zh-CN";
}

function interpolate(source: string, parameters: Parameters): string {
  return source.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name: string) => {
    const value = parameters[name];
    return value === null || value === undefined ? match : String(value);
  });
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", next);
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const applyPreferredLocale = useCallback((next: Locale) => {
    if (!new URLSearchParams(window.location.search).has("lang")) {
      setLocaleState(next);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("coms-platform-locale", locale);
    document.documentElement.lang = locale;
  }, [applyPreferredLocale, locale, setLocale]);

  const value = useMemo<I18nContextValue>(() => {
    const dictionary = dictionaries[locale];
    return {
      locale,
      setLocale,
      applyPreferredLocale,
      has: (key) => Object.hasOwn(dictionary, key),
      t: (key, parameters = {}) =>
        interpolate(dictionary[key] ?? dictionaries.en[key] ?? key, parameters)
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
